/**
 * DRAW BETWEEN SIM TICKS, NOT ON THEM.
 *
 * ── THE DEFECT ─────────────────────────────────────────────────────────────
 *
 * The sim steps on a fixed 60 Hz tick (§3.1) and the client draws at whatever
 * the display gives it — 150-180 fps on the owner's machine. Every draw took the
 * player's position straight off `WorldState`, so the body sat still for two or
 * three frames and then jumped a whole tick's travel in one.
 *
 * The CAMERA does not do that. Its rig is spring-smoothed on the render clock,
 * so it glides. A gliding camera against a stepping body is relative motion the
 * eye reads two ways depending on speed, and the owner reported both:
 *
 *   * at walk and run — "player is randomly jumping"; and
 *   * at sprint — "player is getting blurred when run at speed", because at
 *     8.5 m/s a tick is 142 mm and a sample-and-hold display smears a 142 mm
 *     jump held for 17 ms into exactly that.
 *
 * ── WHY THE FIX IS A LERP AND NOT A FILTER ─────────────────────────────────
 *
 * `FixedStepDriver` has carried `alpha` — "interpolation factor for rendering
 * between the last two sim states" — since it was written, correctly computed
 * as `accumulator / FIXED_DT`, and NOTHING HAS EVER READ IT. The information
 * needed to draw the in-between frames was already there. This is the reader.
 *
 * Rendering at `lerp(previousTick, currentTick, alpha)` is exact: it shows the
 * position the sim WOULD have had at that instant, one tick late. It costs one
 * tick (16.7 ms) of latency and buys continuous motion. A smoothing filter, the
 * usual alternative, is neither exact nor bounded and lags differently at every
 * speed.
 *
 * ⚠ A TELEPORT MUST NOT BE INTERPOLATED. Restarts, kickoffs and `R — ball at
 * feet` move a player metres in one tick, and lerping that would slide the body
 * across the pitch over one frame instead of cutting. Anything faster than
 * `TELEPORT_M` per tick is a cut, and is drawn at the new position immediately.
 */
import { MAX_PLAYERS, type WorldState } from '@fg/shared/sim';

/**
 * A single tick's travel beyond which the move is a CUT, not motion.
 *
 * `MAX_SPEED_SPRINT` is 8.5 m/s, or 142 mm per 60 Hz tick. A metre is seven
 * times that, so nothing a player can do on his feet trips it, and every
 * restart — which moves people tens of metres — does.
 */
const TELEPORT_M = 1.0;

export interface InterpSample {
  x: number;
  y: number;
  z: number;
  yaw: number;
}

/** Shortest-arc angle lerp. A plain lerp takes the long way round at ±π. */
function lerpAngle(a: number, b: number, t: number): number {
  let d = b - a;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return a + d * t;
}

export class TickInterpolator {
  private readonly px = new Float64Array(MAX_PLAYERS);
  private readonly py = new Float64Array(MAX_PLAYERS);
  private readonly pz = new Float64Array(MAX_PLAYERS);
  private readonly yaw = new Float64Array(MAX_PLAYERS);
  private readonly had = new Uint8Array(MAX_PLAYERS);
  private ballX = 0;
  private ballY = 0;
  private ballZ = 0;
  private ballHad = false;
  private readonly out: InterpSample = { x: 0, y: 0, z: 0, yaw: 0 };

  /**
   * Snapshot the world as it is BEFORE a tick runs. Call this at the top of the
   * `onStep` callback handed to `FixedStepDriver.advance`, so that after the
   * driver returns, `this` holds the state one tick behind `world` — which is
   * exactly what `alpha` interpolates from. When several ticks run in one frame
   * it ends up holding the state before the LAST of them, which is still right.
   */
  capture(world: WorldState): void {
    const p = world.players;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (p.active[i] !== 1) {
        this.had[i] = 0;
        continue;
      }
      this.px[i] = p.px[i] as number;
      this.py[i] = p.py[i] as number;
      this.pz[i] = p.pz[i] as number;
      this.yaw[i] = p.yaw[i] as number;
      this.had[i] = 1;
    }
    this.ballX = world.ball.px;
    this.ballY = world.ball.py;
    this.ballZ = world.ball.pz;
    this.ballHad = true;
  }

  /** Forget the history — after a reset, so the next frame cuts rather than slides. */
  reset(): void {
    this.had.fill(0);
    this.ballHad = false;
  }

  /**
   * Where player `id` should be DRAWN this frame. The returned object is reused
   * between calls; read it before calling again.
   */
  player(world: WorldState, id: number, alpha: number): InterpSample {
    const p = world.players;
    const cx = p.px[id] as number;
    const cy = p.py[id] as number;
    const cz = p.pz[id] as number;
    const cyaw = p.yaw[id] as number;
    const o = this.out;
    if (this.had[id] !== 1) {
      o.x = cx; o.y = cy; o.z = cz; o.yaw = cyaw;
      return o;
    }
    const ax = this.px[id] as number;
    const az = this.pz[id] as number;
    if (Math.hypot(cx - ax, cz - az) > TELEPORT_M) {
      o.x = cx; o.y = cy; o.z = cz; o.yaw = cyaw;
      return o;
    }
    const t = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
    o.x = ax + (cx - ax) * t;
    o.y = (this.py[id] as number) + (cy - (this.py[id] as number)) * t;
    o.z = az + (cz - az) * t;
    o.yaw = lerpAngle(this.yaw[id] as number, cyaw, t);
    return o;
  }

  /** Where the ball should be DRAWN this frame. Reuses one object, as above. */
  ball(world: WorldState, alpha: number): InterpSample {
    const b = world.ball;
    const o = this.out;
    if (!this.ballHad) {
      o.x = b.px; o.y = b.py; o.z = b.pz; o.yaw = 0;
      return o;
    }
    // ⚠ THE BALL'S TELEPORT BAR IS ITS OWN. A struck ball leaves the boot at up
    // to ~40 m/s, which is 0.67 m in a tick — well past a player's bar, and
    // ordinary motion for a ball. Judged in three dimensions because a lofted
    // ball's rise is real travel too.
    const d = Math.hypot(b.px - this.ballX, b.py - this.ballY, b.pz - this.ballZ);
    if (d > TELEPORT_M * 3) {
      o.x = b.px; o.y = b.py; o.z = b.pz; o.yaw = 0;
      return o;
    }
    const t = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
    o.x = this.ballX + (b.px - this.ballX) * t;
    o.y = this.ballY + (b.py - this.ballY) * t;
    o.z = this.ballZ + (b.pz - this.ballZ) * t;
    o.yaw = 0;
    return o;
  }
}
